/**
 * How a conversation's messages become rows in the frame's thread.
 *
 * Everything the agent says is shown as its words, in the order it said it:
 * the sentence it opens a turn with, any it says between steps, and its final
 * answer. Each sentence is also spoken, and a reply heard but never shown -
 * folded into the steps disclosure - left the user nothing to read back. The
 * tool steps of a run stay in one "Worked for…" disclosure, placed where the
 * first step ran - or, when the user steered a prompt into the running turn
 * after that, below the last such prompt, so the work it joined reads after it.
 */
import type { FilingReceipt } from "@/lib/filing-receipt";
import type { ConversationMessage } from "@/stores/conversation-store";

export type ThreadItem =
  | {
      kind: "message";
      message: ConversationMessage;
      /** Said along the way, not the run's answer: never an embed anchor. */
      spoken?: boolean;
    }
  | {
      kind: "steps";
      executionId: string;
      steps: ConversationMessage[];
      active: boolean;
      startedAt?: string;
      completedAt?: string;
    }
  | {
      /** Consecutive filing receipts of one run, all in one project. */
      kind: "filing";
      id: string;
      executionId: string;
      receipts: FilingReceipt[];
      running: boolean;
    };

/**
 * A sentence the agent said while working: committed (`progress`, which on a
 * reload arrives as a system row) or still streaming. The final answer is a
 * plain assistant message, and the store drops the progress copy of it when
 * it lands, so nothing is shown twice.
 */
function isSpokenProgress(message: ConversationMessage): boolean {
  return (
    !!message.executionId &&
    (message.activityKind === "progress" ||
      (message.role === "assistant" && message.isStreaming === true))
  );
}

/** A tool, desktop or other work step: belongs in the disclosure. Filing
 *  receipts are their own notice. */
function isWorkStep(message: ConversationMessage): boolean {
  return (
    !!message.executionId &&
    message.activityKind !== undefined &&
    message.activityKind !== "progress" &&
    message.activityKind !== "filing"
  );
}

/** Joins a receipt to the filing notice just before it when that is the
 *  same run's, in the same project; otherwise starts a notice. */
function pushFiling(
  items: ThreadItem[],
  message: ConversationMessage,
  runningExecutionIds: ReadonlySet<string>,
): void {
  const receipt = message.filingReceipt;
  const executionId = message.executionId;
  if (!receipt || !executionId) return;
  const last = items[items.length - 1];
  if (
    last?.kind === "filing" &&
    last.executionId === executionId &&
    last.receipts[0]!.project_id === receipt.project_id
  ) {
    last.receipts.push(receipt);
    return;
  }
  items.push({
    kind: "filing",
    id: message.id,
    executionId,
    receipts: [receipt],
    running: runningExecutionIds.has(executionId),
  });
}

/** A spoken sentence shown as the agent's words. */
function asSpokenMessage(message: ConversationMessage): ConversationMessage {
  return { ...message, role: "assistant", activityKind: undefined };
}

type ExecutionFacts = {
  steps: ConversationMessage[];
  startedAt?: string;
  completedAt?: string;
  final?: ConversationMessage;
};

function collectExecutionFacts(
  messages: readonly ConversationMessage[],
): Map<string, ExecutionFacts> {
  const facts = new Map<string, ExecutionFacts>();
  for (const message of messages) {
    const executionId = message.executionId;
    if (!executionId) continue;
    const entry = facts.get(executionId) ?? { steps: [] };
    facts.set(executionId, entry);
    const start = message.executionStartedAt ?? message.createdAt;
    if (!entry.startedAt || start < entry.startedAt) entry.startedAt = start;
    if (
      message.executionCompletedAt &&
      (!entry.completedAt || message.executionCompletedAt > entry.completedAt)
    ) {
      entry.completedAt = message.executionCompletedAt;
    }
    if (isWorkStep(message)) entry.steps.push(message);
    else if (
      message.role === "assistant" &&
      !message.activityKind &&
      !message.isStreaming
    )
      entry.final = message;
  }
  return facts;
}

/**
 * The message index after which each run's steps disclosure is placed: the
 * later of its first step and the last user prompt steered into the run.
 */
function stepsAnchors(
  messages: readonly ConversationMessage[],
): Map<number, string> {
  const firstStep = new Map<string, number>();
  const lastSteered = new Map<string, number>();
  messages.forEach((message, index) => {
    if (isWorkStep(message) && !firstStep.has(message.executionId!))
      firstStep.set(message.executionId!, index);
    if (message.role === "user" && message.steeredIntoExecutionId)
      lastSteered.set(message.steeredIntoExecutionId, index);
  });
  const anchors = new Map<number, string>();
  for (const [executionId, index] of firstStep) {
    anchors.set(
      Math.max(index, lastSteered.get(executionId) ?? -1),
      executionId,
    );
  }
  return anchors;
}

export function buildThreadItems(
  messages: readonly ConversationMessage[],
  runningExecutionIds: ReadonlySet<string>,
): ThreadItem[] {
  const facts = collectExecutionFacts(messages);
  const anchors = stepsAnchors(messages);
  const items: ThreadItem[] = [];
  messages.forEach((message, index) => {
    if (isWorkStep(message)) {
      // Shown inside its run's disclosure, placed at the run's anchor.
    } else if (message.activityKind === "filing") {
      pushFiling(items, message, runningExecutionIds);
    } else if (isSpokenProgress(message)) {
      items.push({
        kind: "message",
        message: asSpokenMessage(message),
        spoken: true,
      });
    } else if (!message.activityKind) {
      items.push({ kind: "message", message });
    }
    const executionId = anchors.get(index);
    if (!executionId) return;
    const execution = facts.get(executionId)!;
    items.push({
      kind: "steps",
      executionId,
      steps: execution.steps,
      // Running until its answer lands. Not "while something streams": a
      // native run's commentary rows stay marked streaming for good.
      active: !execution.final && runningExecutionIds.has(executionId),
      startedAt: execution.startedAt,
      completedAt:
        execution.final?.executionCompletedAt ??
        execution.completedAt ??
        execution.final?.createdAt,
    });
  });
  return items;
}

/**
 * Where each run's frames are embedded: after its answer, once - never after
 * a sentence said along the way, and not at all for a run without an answer.
 */
export function answerItemByRun(
  items: readonly ThreadItem[],
): Map<string, number> {
  const answers = new Map<string, number>();
  items.forEach((item, index) => {
    if (
      item.kind === "message" &&
      !item.spoken &&
      item.message.role === "assistant" &&
      item.message.executionId
    ) {
      answers.set(item.message.executionId, index);
    }
  });
  return answers;
}
